/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { Permit } from '../../../accessibility/permits';
import {
    AuditorCabinet,
    CentralInspectorCabinet,
    DirectorCabinet,
    ExpertCabinet,
    InspectorCabinet,
    OperatorCabinet,
    RegionalInspectorCabinet,
    SuperCabinet,
    TaxpayerCabinet,
} from '../../../accessibility/permitsMock';
import { BaseFeatureMock } from '../mockServer/featureMock';
import { InspectorLoginFormData, LoginResponseData, OperatorTaxpayerLoginFormData } from './authClient.typings';

const INSPECTOR_LOGIN_URL = /authentication\/inspector$/;
const OPERATOR_LOGIN_URL = /authentication\/operator$/;
const TAXPAYER_LOGIN_URL = /authentication\/taxpayer$/;
const LOGOUT_URL = /authentication\/logout$/;

function formDataFrom(configData: FormData): Record<string, string> {
    return Object.fromEntries(configData) as Record<string, string>;
}

function inspectorPermits(username: string): Permit[] {
    switch (username) {
        case 'director@noogadev.com':
            return DirectorCabinet;
        case 'expert@noogadev.com':
            return ExpertCabinet;
        case 'auditor@noogadev.com':
            return AuditorCabinet;
        case 'super@noogadev.com':
            return SuperCabinet;
        case 'r.inspector@noogadev.com':
            return RegionalInspectorCabinet;
        case 'c.inspector@noogadev.com':
            return CentralInspectorCabinet;
        default:
            return InspectorCabinet;
    }
}

class AuthFeatureMock extends BaseFeatureMock {
    public readonly inspectorLogin = this.route<LoginResponseData, InspectorLoginFormData>({
        method: 'post',
        url: INSPECTOR_LOGIN_URL,
        defaultResponse: (config) => {
            const formData = formDataFrom(config.data as FormData);
            return {
                permits: inspectorPermits(formData.username),
                userId: 1,
                email: 'raman.kremko@noogadev.com',
                firstName: 'Raman',
                middleName: 'Sergeevich',
                lastName: 'Kremko',
            };
        },
        delayMs: 100,
    });

    public readonly operatorLogin = this.route<LoginResponseData, OperatorTaxpayerLoginFormData>({
        method: 'post',
        url: OPERATOR_LOGIN_URL,
        defaultResponse: (config) => {
            const { phoneNumber, personalID } = formDataFrom(config.data as FormData);
            return {
                permits: OperatorCabinet,
                userId: 2,
                phone: phoneNumber,
                firstName: '',
                middleName: '',
                lastName: '',
                companyTaxNumber: personalID.split(':')[1],
            };
        },
        delayMs: 100,
    });

    public readonly taxpayerLogin = this.route<LoginResponseData, OperatorTaxpayerLoginFormData>({
        method: 'post',
        url: TAXPAYER_LOGIN_URL,
        defaultResponse: (config) => {
            const { phoneNumber, personalID } = formDataFrom(config.data as FormData);
            return {
                permits: TaxpayerCabinet,
                userId: 3,
                phone: phoneNumber,
                firstName: 'RamanT',
                middleName: 'SergeevichT',
                lastName: 'KremkoT',
                companyTaxNumber: personalID.split(':')[1],
            };
        },
        delayMs: 100,
    });

    public readonly logout = this.route<void>({
        method: 'post',
        url: LOGOUT_URL,
        defaultResponse: () => undefined,
        delayMs: 50,
    });
}

export const authMock = new AuthFeatureMock();

/** Backward-compatible development mock registration. */
export function addAuthMockApi(mockAdapter: MockAdapter): void {
    authMock.register(mockAdapter, { delay: true });
}
